import { LogOut, Moon, Sun, User, Settings2, X, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { useAuthStore } from '../../stores/authStore.js';
import { authService } from '../../services/auth.service.js';
import { useNavigate } from 'react-router-dom';
import { useUIStore } from '../../stores/uiStore.js';
import { cn } from '../../lib/utils.js';

const headerLeftMap = {
  expanded: 'left-64',
  collapsed: 'left-16',
  hidden: 'left-0',
} as const;

export function Header() {
  const { user, logout } = useAuthStore();
  const sidebarState = useUIStore((s) => s.sidebarState);
  const { darkMode, toggleDarkMode } = useUIStore();
  const { contrast, setContrast, brightness, setBrightness, resetVisualAdjustments } = useUIStore();
  const navigate = useNavigate();

  const [showVisualPanel, setShowVisualPanel] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Close panel on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setShowVisualPanel(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch {
      // ignore
    }
    logout();
    navigate('/login');
  };

  return (
    <header
      className={cn(
        'fixed right-0 top-0 z-20 flex h-16 items-center justify-end border-b border-gray-200 bg-white px-6 transition-all duration-300 ease-in-out dark:border-gray-700 dark:bg-gray-900',
        headerLeftMap[sidebarState],
      )}
    >
      <div className="flex items-center gap-3">
        {/* Visual Adjustments Button */}
        <div className="relative" ref={panelRef}>
          <button
            onClick={() => setShowVisualPanel((v) => !v)}
            className={cn(
              'rounded-lg p-2 transition-colors',
              showVisualPanel || contrast !== 1 || brightness !== 1
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800',
            )}
            title="Görsel Ayarlar"
          >
            <Settings2 className="h-5 w-5" />
          </button>

          {/* Visual Adjustments Panel */}
          {showVisualPanel && (
            <div className="absolute right-0 top-full z-50 mt-2 w-72 rounded-xl border border-gray-200 bg-white p-4 shadow-xl dark:border-gray-700 dark:bg-gray-800">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Görsel Ayarlar</h3>
                <button
                  onClick={() => setShowVisualPanel(false)}
                  className="rounded p-0.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Contrast Slider */}
              <div className="mb-4">
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Kontrast</label>
                  <span className="text-xs font-mono text-gray-500 dark:text-gray-400">{contrast.toFixed(2)}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400">Düşük</span>
                  <input
                    type="range"
                    min="0.5"
                    max="1.5"
                    step="0.05"
                    value={contrast}
                    onChange={(e) => setContrast(parseFloat(e.target.value))}
                    className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 accent-primary-600 dark:bg-gray-600"
                  />
                  <span className="text-xs text-gray-400">Yüksek</span>
                </div>
              </div>

              {/* Brightness Slider */}
              <div className="mb-4">
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Parlaklık</label>
                  <span className="text-xs font-mono text-gray-500 dark:text-gray-400">{brightness.toFixed(2)}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400">Koyu</span>
                  <input
                    type="range"
                    min="0.5"
                    max="1.5"
                    step="0.05"
                    value={brightness}
                    onChange={(e) => setBrightness(parseFloat(e.target.value))}
                    className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 accent-primary-600 dark:bg-gray-600"
                  />
                  <span className="text-xs text-gray-400">Parlak</span>
                </div>
              </div>

              {/* Reset Button */}
              {(contrast !== 1 || brightness !== 1) && (
                <button
                  onClick={resetVisualAdjustments}
                  className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
                >
                  Sıfırla
                </button>
              )}

              {/* Info Text */}
              <p className="mt-3 text-[10px] text-gray-400 dark:text-gray-500">
                Bu ayarlar ekranın kontrast ve parlaklık seviyesini değiştirir. Göz yorgunluğunu azaltmak için kullanın.
              </p>
            </div>
          )}
        </div>

        {/* Dark Mode Toggle */}
        <button
          onClick={toggleDarkMode}
          className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
          title={darkMode ? 'Açık mod' : 'Koyu mod'}
        >
          {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </button>
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <User className="h-4 w-4" />
          <span>{user?.firstName} {user?.lastName}</span>
          <span className="rounded bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900 dark:text-primary-300">
            {user?.role}
          </span>
        </div>
        <Link
          to="/settings/security"
          title={user?.totpEnabledAt ? '2FA aktif — güvenlik ayarları' : '2FA kapalı — güvenlik ayarları'}
          className={`rounded-lg p-2 transition-colors ${
            user?.totpEnabledAt
              ? 'text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/20'
              : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
          }`}
        >
          <ShieldCheck className="h-5 w-5" />
        </Link>
        <button
          onClick={handleLogout}
          className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600 dark:text-gray-400 dark:hover:bg-red-900/30 dark:hover:text-red-400"
        >
          <LogOut className="h-4 w-4" />
          Çıkış
        </button>
      </div>
    </header>
  );
}
